/**
 * Головна книга (ч.1038): кожен рахунок з рухами → періоди → кореспонденція.
 * Клік по рахунку — обороти рахунку; по періоду — картка за період.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { reports, type PeriodRow } from '@/api/documents';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import LedgerFilters, { NoRegulatedAccess } from './LedgerFilters';
import { useLedgerParams } from './useLedgerParams';
import { balanceColumns, periodLabel, TreeLabel, LABEL_W } from './ledgerColumns';
import { ledgerLinks, PERIODICITY, toggleCollapsed, visibleRows } from './ledgerModel';

type Row = PeriodRow & { _idx: string };

export default function GeneralLedgerPage() {
  const st = useLedgerParams();
  const navigate = useNavigate();
  const per = st.get('per') || 'month';
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const q = useQuery({
    queryKey: ['ledger-gl', st.org, st.from, st.to, per],
    queryFn: () => reports.generalLedger({ organization_id: st.org, date_from: st.from, date_to: st.to, periodicity: per }),
    enabled: !!st.org,
  });
  const data = q.data;
  // Кореспонденцію всередині періодів згортаємо за замовчуванням — інакше
  // книга на 80 рахунків × 12 місяців × 5 кор. рахунків не читається.
  useEffect(() => {
    if (data) setCollapsed(new Set(data.rows.map((r, i) => (r.kind === 'period' && r.is_group ? String(i) : '')).filter(Boolean)));
  }, [data]);
  const rows = useMemo<Row[]>(() => data
    ? visibleRows(data.rows.map((r, i) => ({ ...r, _idx: String(i) })), collapsed, (r) => r._idx) : [], [data, collapsed]);
  const link = (r: Row) => r.kind === 'account'
    ? ledgerLinks.accountTurnover({ org: st.org, from: st.from, to: st.to, account: r.account_code, per })
    : ledgerLinks.accountCard({ org: st.org, from: r.period_start, to: r.period_end, account: r.account_code, corr: r.corr_account_code });

  const cols = useMemo<ColumnDef<Row>[]>(() => [
    { key: 'label', header: 'Рахунок / період / кор. рахунок', width: LABEL_W,
      cell: (r) => (
        <TreeLabel level={r.level} isGroup={r.is_group} collapsed={collapsed.has(r._idx)}
          onToggle={() => setCollapsed((s) => toggleCollapsed(s, r._idx))}>
          <a className="ledger__link" title={r.kind === 'account' ? 'Обороти рахунку' : 'Картка рахунку за період'}
            onClick={(e) => { e.stopPropagation(); navigate(link(r)); }}>{r.label}</a>
        </TreeLabel>) },
    ...balanceColumns<Row>(),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [collapsed, st.org, st.from, st.to, per]);

  if (!st.permsLoading && !st.hasPermission) return <NoRegulatedAccess title="Головна книга" total={st.totalAccessibleOrgs} />;
  const accountIdx = data ? data.rows.map((r, i) => (r.kind === 'account' ? String(i) : '')).filter(Boolean) : [];
  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Головна книга{' '}<span className="ledger__subtitle">{periodLabel(st.from, st.to)}</span></h2>
      </div>
      <LedgerFilters state={st}
        printHref={ledgerLinks.print('gl', { org: st.org, from: st.from, to: st.to, per })}
        onExcel={() => reports.ledgerXlsx('/reports/general-ledger', { organization_id: st.org, date_from: st.from, date_to: st.to, periodicity: per })}
        disabled={!data}>
        <div className="field">
          <label>Період</label>
          <select value={per} onChange={(e) => st.set({ per: e.target.value === 'month' ? null : e.target.value })}>
            {PERIODICITY.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
        <span className="ledger__tree-actions">
          <button type="button" className="btn-link" onClick={() => setCollapsed(new Set(accountIdx))}>лише рахунки</button>
          <button type="button" className="btn-link" onClick={() => setCollapsed(new Set(data?.rows.map((r, i) => (r.kind === 'period' ? String(i) : '')).filter(Boolean)))}>рахунки і періоди</button>
          <button type="button" className="btn-link" onClick={() => setCollapsed(new Set())}>усе</button>
        </span>
      </LedgerFilters>
      {q.isError && <p className="ledger__warn">Не вдалося завантажити книгу.</p>}
      <DataTable<Row>
        rows={rows} columns={cols} rowKey={(r) => r._idx} loading={q.isLoading}
        emptyText="За період проводок немає."
        rowBold={(r) => r.kind === 'account'}
        rowBackground={(r) => r.kind === 'account' ? 'var(--bg-soft)' : undefined}
        onRowDoubleClick={(r) => navigate(link(r))}
        storageKey="ledger-general-ledger"
      />
      {data && <div className="ledger__totals"><span className="ledger__hint">клік по рахунку — обороти рахунку; по періоду — картка за період</span></div>}
    </div>
  );
}
